<template>
  <div class="gmail-sidebar">
    <!-- Não conectado -->
    <div v-if="!status.connected" class="gmail-sidebar__empty">
      <i class="iconoir-send-mail gmail-sidebar__empty-icon"></i>
      <p class="gmail-sidebar__empty-text">
        {{ $t('gmailSidebar.notConnected') }}
      </p>
      <Button type="primary" :loading="loading" @click="connect()">
        {{ $t('gmailSidebar.connect') }}
      </Button>
    </div>

    <template v-else>
      <!-- Ações -->
      <div class="gmail-sidebar__actions">
        <a
          class="gmail-sidebar__action"
          :class="{ 'gmail-sidebar__action--active': mode === 'compose' }"
          @click.prevent="mode = mode === 'compose' ? 'list' : 'compose'"
        >
          <i class="iconoir-send-mail"></i>
          {{ $t('gmailSidebar.compose') }}
        </a>
        <a
          class="gmail-sidebar__action"
          :class="{ 'gmail-sidebar__action--active': mode === 'link' }"
          @click.prevent="toggleLinkMode()"
        >
          <i class="iconoir-link"></i>
          {{ $t('gmailSidebar.linkThread') }}
        </a>
      </div>

      <!-- Compor -->
      <form
        v-if="mode === 'compose'"
        class="gmail-sidebar__compose"
        @submit.prevent="sendEmail()"
      >
        <input
          v-model="compose.to"
          class="gmail-sidebar__input"
          type="email"
          required
          :placeholder="$t('gmailSidebar.to')"
        />
        <input
          v-model="compose.subject"
          class="gmail-sidebar__input"
          required
          :placeholder="$t('gmailSidebar.subject')"
        />
        <textarea
          v-model="compose.body"
          class="gmail-sidebar__textarea"
          rows="6"
          :placeholder="$t('gmailSidebar.body')"
        ></textarea>
        <div class="gmail-sidebar__compose-actions">
          <span class="gmail-sidebar__from"
            >{{ $t('gmailSidebar.as') }} {{ status.email_address }}</span
          >
          <Button type="primary" :loading="sending">
            {{ $t('gmailSidebar.send') }}
          </Button>
        </div>
      </form>

      <!-- Vincular -->
      <div v-if="mode === 'link'" class="gmail-sidebar__link">
        <input
          ref="searchInput"
          v-model="search"
          class="gmail-sidebar__input"
          :placeholder="$t('gmailSidebar.searchPlaceholder')"
          @input="scheduleSearch()"
        />
        <div v-if="searching" class="gmail-sidebar__muted">
          {{ $t('gmailSidebar.searching') }}
        </div>
        <a
          v-for="thread in searchResults"
          :key="thread.thread_id"
          class="gmail-sidebar__thread"
          @click.prevent="linkThread(thread)"
        >
          <div class="gmail-sidebar__thread-subject">
            {{ thread.subject }}
            <span class="gmail-sidebar__thread-count"
              >({{ thread.message_count }})</span
            >
          </div>
          <div class="gmail-sidebar__thread-meta">{{ thread.last_from }}</div>
          <div class="gmail-sidebar__thread-snippet">{{ thread.snippet }}</div>
        </a>
      </div>

      <!-- Conversas vinculadas -->
      <div class="gmail-sidebar__links">
        <div v-if="links.length === 0 && mode === 'list'" class="gmail-sidebar__muted">
          {{ $t('gmailSidebar.noLinks') }}
        </div>
        <div v-for="link in links" :key="link.id" class="gmail-sidebar__card">
          <a class="gmail-sidebar__card-head" @click.prevent="toggleThread(link)">
            <i
              :class="
                openThreadId === link.id
                  ? 'iconoir-nav-arrow-down'
                  : 'iconoir-nav-arrow-right'
              "
            ></i>
            <div class="gmail-sidebar__card-title">
              <div class="gmail-sidebar__thread-subject">{{ link.subject }}</div>
              <div class="gmail-sidebar__thread-meta">
                {{ link.last_from }} ·
                {{ $t('gmailSidebar.linkedBy', { name: link.linked_by }) }}
              </div>
            </div>
            <a
              class="gmail-sidebar__card-remove"
              :title="$t('gmailSidebar.unlink')"
              @click.stop.prevent="unlink(link)"
            >
              <i class="iconoir-cancel"></i>
            </a>
          </a>
          <div v-if="openThreadId === link.id" class="gmail-sidebar__messages">
            <div v-if="threadLoading" class="gmail-sidebar__muted">
              {{ $t('gmailSidebar.loadingThread') }}
            </div>
            <div
              v-for="(message, index) in threadMessages"
              :key="index"
              class="gmail-sidebar__message"
            >
              <div class="gmail-sidebar__message-head">
                <strong>{{ message.from }}</strong>
                <span>{{ message.date }}</span>
              </div>
              <div class="gmail-sidebar__message-body">{{ message.body }}</div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import GmailService from '@baserow/modules/core/services/gmail'
import { notifyIf } from '@baserow/modules/core/utils/error'

export default {
  name: 'GmailRowSidebar',
  props: {
    row: { type: Object, required: true },
    table: { type: Object, required: true },
    database: { type: Object, required: true },
    view: { type: Object, required: false, default: null },
  },
  data() {
    return {
      loading: false,
      status: { connected: false, email_address: '' },
      mode: 'list',
      compose: { to: '', subject: '', body: '' },
      sending: false,
      search: '',
      searching: false,
      searchResults: [],
      links: [],
      openThreadId: null,
      threadMessages: [],
      threadLoading: false,
    }
  },
  watch: {
    'row.id'() {
      this.fetchLinks()
    },
  },
  async mounted() {
    try {
      const { data } = await GmailService(this.$client).status()
      this.status = data
    } catch (error) {
      // segue como não conectado
    }
    await this.fetchLinks()
    if (this.$route.query.gmail === 'connected') {
      this.$store.dispatch('toast/success', {
        title: this.$t('gmailSidebar.connectedToastTitle'),
        message: this.$t('gmailSidebar.connectedToast'),
      })
    }
  },
  beforeUnmount() {
    clearTimeout(this.searchTimeout)
  },
  methods: {
    async connect() {
      this.loading = true
      try {
        const { data } = await GmailService(this.$client).authUrl(
          this.$route.path
        )
        window.location.href = data.auth_url
      } catch (error) {
        this.loading = false
        notifyIf(error, 'application')
      }
    },
    async fetchLinks() {
      if (!Number.isInteger(this.row.id)) {
        return
      }
      try {
        const { data } = await GmailService(this.$client).links(
          this.table.id,
          this.row.id
        )
        this.links = data.links
      } catch (error) {
        this.links = []
      }
    },
    async sendEmail() {
      this.sending = true
      try {
        await GmailService(this.$client).send(
          this.compose.to,
          this.compose.subject,
          this.compose.body
        )
        this.$store.dispatch('toast/success', {
          title: this.$t('gmailSidebar.sentToastTitle'),
          message: this.$t('gmailSidebar.sentToast', { to: this.compose.to }),
        })
        // Registra o envio como comentário para a equipe ver.
        this.postSentComment()
        this.compose = { to: '', subject: '', body: '' }
        this.mode = 'list'
      } catch (error) {
        notifyIf(error, 'application')
      } finally {
        this.sending = false
      }
    },
    postSentComment() {
      const text = this.$t('gmailSidebar.sentComment', {
        to: this.compose.to,
        subject: this.compose.subject,
      })
      this.$store
        .dispatch('row_comments/postComment', {
          tableId: this.table.id,
          rowId: this.row.id,
          message: {
            type: 'doc',
            content: [
              {
                type: 'paragraph',
                content: [{ type: 'text', text: `📧 ${text}` }],
              },
            ],
          },
          viewId: this.view?.id,
        })
        .catch(() => {})
    },
    toggleLinkMode() {
      this.mode = this.mode === 'link' ? 'list' : 'link'
      if (this.mode === 'link') {
        this.searchThreads()
        this.$nextTick(() => this.$refs.searchInput?.focus())
      }
    },
    scheduleSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => this.searchThreads(), 400)
    },
    async searchThreads() {
      this.searching = true
      try {
        const { data } = await GmailService(this.$client).search(this.search)
        this.searchResults = data.results
      } catch (error) {
        this.searchResults = []
      } finally {
        this.searching = false
      }
    },
    async linkThread(thread) {
      try {
        await GmailService(this.$client).createLink(
          this.table.id,
          this.row.id,
          thread
        )
        this.mode = 'list'
        await this.fetchLinks()
      } catch (error) {
        notifyIf(error, 'application')
      }
    },
    async toggleThread(link) {
      if (this.openThreadId === link.id) {
        this.openThreadId = null
        return
      }
      this.openThreadId = link.id
      this.threadMessages = []
      this.threadLoading = true
      try {
        const { data } = await GmailService(this.$client).linkDetail(link.id)
        this.threadMessages = data.messages
      } catch (error) {
        notifyIf(error, 'application')
      } finally {
        this.threadLoading = false
      }
    },
    async unlink(link) {
      try {
        await GmailService(this.$client).deleteLink(link.id)
        await this.fetchLinks()
      } catch (error) {
        notifyIf(error, 'application')
      }
    },
  },
}
</script>
